import api from './api';

class InspectionService {
    // Obtener inspecciones
    async getInspections(filters = {}) {
        try {
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.insuranceCompany) params.append('insuranceCompany', filters.insuranceCompany);

            const response = await api.get(`/inspections?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error getting inspections:', error);
            throw new Error(error.response?.data?.error || 'Error al obtener las inspecciones');
        }
    }

    // Crear nueva inspección
    async createInspection(inspectionData) {
        try {
            const response = await api.post('/inspections', inspectionData);
            return response.data;
        } catch (error) {
            console.error('Error creating inspection:', error);
            throw new Error(error.response?.data?.error || 'Error al crear la inspección');
        }
    }

    async getInspection(inspectionId) {
        try {
            const response = await api.get(`/inspections/${inspectionId}`);
            return response.data;
        } catch (error) {
            console.error('Error getting inspection:', error);
            throw new Error(error.response?.data?.error || 'Error al obtener la inspección');
        }
    }

    async getInspectionPhotos(inspectionId) {
        try {
            const response = await api.get(`/inspections/${inspectionId}/photos`);
            return response.data;
        } catch (error) {
            console.error('Error getting inspection photos:', error);
            throw new Error(error.response?.data?.error || 'Error al obtener las fotos de la inspección');
        }
    }

    async deleteInspection(inspectionId) {
        try {
            const response = await api.delete(`/inspections/${inspectionId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting inspection:', error);
            throw new Error(error.response?.data?.error || 'Error al borrar la inspección');
        }
    }


    async updateInspection(inspectionId, updatedInspection) {
        try {
            const response = await api.put(`/inspections/${inspectionId}`, updatedInspection);
            return response.data;
        } catch (error) {
            console.error('Error updating inspection:', error);
            throw new Error(error.response?.data?.error || 'Error al actualizar la inspección');
        }
    }

    async updateInspectionInformation(inspectionId, changes) {
        try {
            const response = await api.put(`/inspections/${inspectionId}/information`, changes);
            return response.data;
        } catch (error) {
            console.error('Error updating inspection information:', error);
            throw new Error(error.response?.data?.error || 'Error al corregir la información de la inspección');
        }
    }

    async getInspectionStats(filters = {}) {
        try {
            const params = new URLSearchParams();
            if (filters.insuranceCompany) params.append('insuranceCompany', filters.insuranceCompany);

            const response = await api.get(`/inspections/stats?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error getting inspection stats:', error);
            throw new Error(error.response?.data?.error || 'Error al obtener estadísticas');
        }
    }

    async getPendingInspections(params = {}) {
        try {
            const queryParams = new URLSearchParams();
            
            if (params.insuranceCompany) {
                queryParams.append('insuranceCompany', params.insuranceCompany);
            }

            const response = await api.get(`/inspections/pending?${queryParams.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching pending inspections:', error);
            throw new Error(error.response?.data?.error || 'Error al obtener las inspecciones pendientes');
        }
    }

    async finalizeInspection(inspectionId) {
        try {
            const response = await api.post(`/inspections/${inspectionId}/finalize`);
            return response.data;
        } catch (error) {
            console.error('Error finalizando inspección:', error);
            throw new Error(error.response?.data?.error || 'Error al finalizar la inspección');
        }
    }

    async requestAdditionalPhotos(inspectionId, inspectorComment, inspectorName) {
        try {
            const response = await api.post(`/inspections/${inspectionId}/request-additional-photos`, {
                inspectionId,
                inspectorComment,
                inspectorName
            });
            return response.data;
        } catch (error) {
            console.error('Error requestando fotos adicionales:', error);
            throw new Error(error.response?.data?.error || 'Error al requestar fotos adicionales');
        }
    }

    async notifyPhotosRejected(inspectionId, rejectedPhotos) {
        try {
            const response = await api.post(`/inspections/${inspectionId}/notify-photos-rejected`, {
                inspectionId,
                rejectedPhotos
            });
            return response.data;
        } catch (error) {
            console.error('Error notificando fotos rechazadas:', error);
            throw new Error(error.response?.data?.error || 'Error al notificar fotos rechazadas');
        }
    }

    async resendEmail(inspectionId, newEmail = null) {
        try {
            const response = await api.post(`/inspections/${inspectionId}/resend-email`, {
                newEmail
            });
            return response.data;
        } catch (error) {
            console.error('Error reenviando email:', error);
            throw error;
        }
    }

    async generateReport(startDate, endDate) {
        try {
            const response = await api.post('/inspections/generate-report', {
                startDate,
                endDate
            });
            return response.data;
        } catch (error) {
            console.error('Error generating report:', error);
            throw new Error(error.response?.data?.error || 'Error al generar el reporte');
        }
    }

    async crossReferencePlatesReport(fileContent, fileName) {
        try {
            const response = await api.post('/inspections/cross-reference-plates', {
                fileContent,
                fileName,
            });
            return response.data;
        } catch (error) {
            console.error('Error cross referencing plates:', error);
            throw new Error(error.response?.data?.error || 'Error al procesar las placas');
        }
    }

    async generateInspectionReport(inspectionId) {
        const response = await api.get(`/inspections/${inspectionId}/report`);
        return response.data;
    }

    async generatePropertyCardUploadUrls() {
        const response = await api.post('/property-card/upload-urls');
        return response.data;
    }

    async readPropertyCard(sessionId) {
        try {
            const response = await api.post('/property-card/read', { sessionId });
            return response.data;
        } catch (error) {
            const data = error.response?.data;
            if (error.response?.status === 422 && data?.propertyCard) {
                return {
                    sessionId,
                    propertyCard: data.propertyCard,
                    missingFields: data.missingFields || [],
                };
            }
            const readError = new Error(data?.error || 'No se pudo leer el documento');
            readError.status = error.response?.status;
            readError.missingFields = data?.missingFields;
            readError.propertyCard = data?.propertyCard;
            throw readError;
        }
    }

    async consultOfficialRecords(inspectionId, payload) {
        try {
            const response = await api.post(
                `/inspections/${inspectionId}/antecedents/official-records`,
                payload
            );
            return response.data;
        } catch (error) {
            const data = error.response?.data;
            const isTimeout = error.code === 'ECONNABORTED' || error.response?.status === 504;
            const fallbackMessage = isTimeout
                ? 'Tiempo de espera agotado al consultar los antecedentes oficiales.'
                : 'Error al consultar los antecedentes oficiales';
            const consultError = new Error(data?.error || fallbackMessage);
            consultError.officialRecords = data?.officialRecords || {
                error: {
                    message: consultError.message,
                    at: new Date().toISOString(),
                    consultBy: payload?.consultBy,
                    code: isTimeout ? 'timeout' : undefined,
                    statusCode: error.response?.status || (isTimeout ? 504 : 502),
                    sourceMessage: isTimeout
                        ? 'La fuente oficial tardó demasiado en responder y se agotó el tiempo de espera.'
                        : undefined,
                },
            };
            throw consultError;
        }
    }

    async validateInspection(inspectionId) {
        try {
            const response = await api.get(`/inspections/${inspectionId}/validate`);
            return response.data;
        } catch (error) {
            console.error('Error validating inspection:', error);
            throw new Error(error.response?.data?.error || 'Error al validar la inspección');
        }
    }

}

const inspectionService = new InspectionService();
export default inspectionService;